import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, StyleSheet, TouchableOpacity, View } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useDispatch, useStore } from 'react-redux';
import Typography from '../../components/Reusables/Typography';
import { AuthContext } from '../../Context/AuthContext';
import useAuth from '../../hooks/useAuth';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { getEmployeeProfileDetails } from '../../api/employees';
import {
  addInAppBusinessInfo,
  addInAppEmployeeInfo,
} from '../../redux/AuthSlice';
import { persistor, type AppDispatch } from '../../redux/Store';
import {
  clearOwnerSetupHandoffMarker,
  readOwnerSetupHandoffMarker,
} from '../../services/ownerSetupSessionHandoff';
import {
  preloadEmployeeSession,
  preloadOwnerSession,
} from '../../services/postLoginPreload';
import {
  runOwnerSetupSessionRecovery,
  type OwnerSetupRecoveryOutcome,
} from './ownerSetupSessionRecovery';
import useBackofficeAxios from '../../hooks/useBackofficeAxios';
import useBackofficeOwnerAuth from '../../hooks/useBackofficeOwnerAuth';
import useBackofficeRefresh from '../../hooks/useBackofficeRefresh';
import { setEmployeeEmail } from '../../services/SecureCredentials';
import { GTFonts, theme } from '../../theme';
import { fs, s, v } from '../../utils/libs';

/**
 * Branded full-screen gate that recovers an interrupted new-owner setup
 * session (US1, T033). Rendered by AppNav instead of Auth/App whenever the
 * owner-setup entry decision is `session_recovery` or a handoff marker
 * exists. Never exposes Home behind it.
 */

type Phase =
  | { name: 'running' }
  | { name: 'error'; error: 'network' | 'profile_unavailable' };

export interface OwnerSetupSessionBootstrapProps {
  /** Terminal outcomes; parent re-evaluates routing. */
  onFinished: (outcome: OwnerSetupRecoveryOutcome) => void;
}

const OwnerSetupSessionBootstrap: React.FC<OwnerSetupSessionBootstrapProps> = ({
  onFinished,
}) => {
  const dispatch = useDispatch();
  const store = useStore();
  const axiosprivate = useAxiosPrivate();
  const axiosBackoffice = useBackofficeAxios();
  const { autoLoginOwner } = useBackofficeOwnerAuth();
  const { clearOwnerSession } = useBackofficeRefresh();
  const { setAuth } = useAuth();
  const { setTokenAssistant } = useContext(AuthContext);
  const [phase, setPhase] = useState<Phase>({ name: 'running' });
  const runningRef = useRef(false);

  const runRecovery = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    setPhase({ name: 'running' });
    try {
      let profileEmail = '';
      const outcome = await runOwnerSetupSessionRecovery({
        dispatch,
        getActivationSnapshot: () => {
          const state = store.getState() as any;
          return {
            ownerKey: state.ownerActivation.ownerKey,
            eligibility: state.ownerActivation.eligibility,
          };
        },
        readHandoffMarker: readOwnerSetupHandoffMarker,
        clearHandoffMarker: clearOwnerSetupHandoffMarker,
        readStoredTokens: async () => {
          const [[, accessToken], [, refreshToken]] =
            await AsyncStorage.multiGet(['accessToken', 'refreshToken']);
          if (!accessToken || !refreshToken) return null;
          return { accessToken, refreshToken };
        },
        clearStoredTokens: async () => {
          await AsyncStorage.multiRemove(['accessToken', 'refreshToken']);
        },
        flushPersistor: () => persistor.flush(),
        fetchEmployeeProfile: token =>
          getEmployeeProfileDetails(axiosprivate, { accessToken: token }),
        isAuthRejection: (error: any) => {
          const status = error?.response?.status;
          return status === 401 || status === 403;
        },
        applyEmployeeProfile: profileResponse => {
          const data = profileResponse?.data?.data;
          profileEmail = String(
            data?.bp_employee_email_address || '',
          ).trim();
          dispatch(addInAppEmployeeInfo(data));
          dispatch(
            addInAppBusinessInfo({
              name: profileResponse?.data?.businessInformation
                ?.bp_user_business_name,
              description:
                profileResponse?.data?.businessInformation
                  ?.bp_user_business_description,
              Adrress: profileResponse?.data?.addressInformation,
              type: profileResponse?.data?.businessInformation
                ?.bp_user_business_type,
            }),
          );
          return String(data?._id || '');
        },
        preloadSession: async (token, employeeId, ownerId) => {
          // Same sequence as LoginScreen post-login preload for owners.
          const locationAfterPreload = await preloadEmployeeSession({
            dispatch: dispatch as AppDispatch,
            axiosPrivate: axiosprivate,
            accessToken: token,
            employeeId,
          });

          if (profileEmail) {
            await setEmployeeEmail(profileEmail);
          }

          await clearOwnerSession();
          const ownerResult = await autoLoginOwner();
          if (
            ownerResult &&
            'accessToken' in ownerResult &&
            ownerResult.accessToken
          ) {
            await preloadOwnerSession({
              dispatch: dispatch as AppDispatch,
              axiosBackoffice,
              ownerAccessToken: ownerResult.accessToken,
              ownerId,
              currentLocationId: locationAfterPreload.currentLocationId,
            });
          }
        },
        setAuthTokens: (access, refresh) => {
          setTokenAssistant({ accessToken: access, refreshToken: refresh });
          setAuth({ accessToken: access, refreshToken: refresh });
        },
        now: () => new Date().toISOString(),
      });

      if (outcome.kind === 'recoverable_error') {
        setPhase({ name: 'error', error: outcome.error });
      } else {
        onFinished(outcome);
      }
    } finally {
      runningRef.current = false;
    }
  }, [
    autoLoginOwner,
    axiosBackoffice,
    axiosprivate,
    clearOwnerSession,
    dispatch,
    onFinished,
    setAuth,
    setTokenAssistant,
    store,
  ]);

  useEffect(() => {
    void runRecovery();
    // Run once on mount; retries are explicit via the Retry button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signInAgain = useCallback(async () => {
    await AsyncStorage.multiRemove(['accessToken', 'refreshToken']);
    await clearOwnerSetupHandoffMarker();
    onFinished({ kind: 'requires_login' });
  }, [onFinished]);

  return (
    <View style={styles.container}>
      {phase.name === 'running' ? (
        <>
          <ActivityIndicator size="large" color={theme.colors.success} />
          <Typography style={styles.title}>
            Finishing your account setup
          </Typography>
          <Typography style={styles.subtitle}>
            This will only take a moment.
          </Typography>
        </>
      ) : (
        <>
          <Typography style={styles.title}>
            We couldn't finish setting up
          </Typography>
          <Typography style={styles.subtitle}>
            {phase.error === 'network'
              ? 'Please check your internet connection and try again.'
              : 'Your profile is temporarily unavailable. Please try again.'}
          </Typography>
          <TouchableOpacity
            style={styles.primaryButton}
            onPress={() => void runRecovery()}
            accessibilityRole="button"
            accessibilityLabel="Retry finishing account setup">
            <Typography style={styles.primaryButtonText}>Retry</Typography>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.secondaryButton}
            onPress={() => void signInAgain()}
            accessibilityRole="button"
            accessibilityLabel="Sign in again">
            <Typography style={styles.secondaryButtonText}>
              Sign in again
            </Typography>
          </TouchableOpacity>
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.surface,
    padding: s(24),
  },
  title: {
    marginTop: v(16),
    fontSize: fs(18),
    lineHeight: fs(24),
    fontFamily: GTFonts.semibold,
    color: theme.colors.text,
    textAlign: 'center',
  },
  subtitle: {
    marginTop: v(8),
    fontSize: fs(14),
    lineHeight: fs(20),
    fontFamily: GTFonts.regular,
    color: theme.colors.text,
    textAlign: 'center',
  },
  primaryButton: {
    marginTop: v(24),
    backgroundColor: theme.colors.primary,
    borderRadius: s(8),
    paddingVertical: v(12),
    paddingHorizontal: s(32),
    minWidth: s(200),
    alignItems: 'center',
  },
  primaryButtonText: {
    color: theme.colors.textInverse,
    fontSize: fs(16),
    lineHeight: fs(20),
    fontFamily: GTFonts.semibold,
  },
  secondaryButton: {
    marginTop: v(12),
    paddingVertical: v(12),
    paddingHorizontal: s(32),
    minWidth: s(200),
    alignItems: 'center',
  },
  secondaryButtonText: {
    color: theme.colors.primary,
    fontSize: fs(16),
    lineHeight: fs(20),
    fontFamily: GTFonts.regular,
  },
});

export default OwnerSetupSessionBootstrap;
